// Render wrapper used by ListPlugin's `render.belowNodes`. Plate's v53 list
// model is indent-based: any block can carry `listStyleType` + `indent` and
// this wrapper paints the surrounding <ul>/<ol> + <li>.
// Simplified from Plate's reference BlockList (todo/check items deferred).

export const BlockList: any = (props: any) => {
    if (!props.element?.listStyleType) return undefined;
    return (innerProps: any) => <List {...innerProps} />;
};

const ORDERED = ['decimal', 'decimal-leading-zero', 'lower-roman', 'upper-roman', 'lower-alpha', 'upper-alpha'];
const BULLET_CYCLE = ['disc', 'circle', 'square'];
const NUMBERED_CYCLE = ['decimal', 'lower-alpha', 'lower-roman'];

function isOrderedListStyle(listStyleType: string) {
    return ORDERED.includes(listStyleType);
}

// Cycle the marker style by indent depth so nested levels read as a
// hierarchy — disc → circle → square for bullets, 1. → a. → i. for numbers.
// The stored listStyleType stays as-is on the node; we only swap the
// RENDERED marker. Avoids touching the save format.
function styleByIndent(base: string, indent: number | undefined): string {
    const depth = Math.max(0, (indent ?? 1) - 1);
    if (BULLET_CYCLE.includes(base)) {
        return BULLET_CYCLE[depth % BULLET_CYCLE.length];
    }
    if (NUMBERED_CYCLE.includes(base)) {
        return NUMBERED_CYCLE[depth % NUMBERED_CYCLE.length];
    }
    return base;
}

function List(props: any) {
    const { listStart, listStyleType, indent, lineHeight, align } = props.element;
    const effectiveStyle = styleByIndent(listStyleType, indent);
    const Tag = isOrderedListStyle(listStyleType) ? 'ol' : 'ul';

    // Propagate LineHeightPlugin's `lineHeight` block prop onto the <ul>/<li>
    // chrome so the marker (rendered against the <li>'s line-box) shifts in
    // step with the text inside the <p>. Without this, Plate's inject only
    // styles the inner <p>, leaving the bullet pinned to a 1.2 line-box even
    // when the user picks 2.
    const ulStyle: React.CSSProperties = { listStyleType: effectiveStyle, padding: 0 };
    const liStyle: React.CSSProperties = {};
    if (lineHeight != null) {
        ulStyle.lineHeight = String(lineHeight);
        liStyle.lineHeight = String(lineHeight);
    }

    // Make the marker move with the text on right/center/justify aligned
    // items. Default `list-style-position: outside` keeps the bullet in a
    // gutter to the left of the <li>; switching to `inside` makes it part
    // of the content flow, and applying `text-align` on the <li> + inlining
    // the <p> brings marker + text together to the chosen edge.
    if (align && align !== 'left') {
        liStyle.textAlign = align;
        liStyle.listStylePosition = 'inside';
    }

    // Only tag `data-align` for non-left alignments. The PDF override's
    // `li[data-align] { list-style-position: inside }` rule otherwise pulls
    // the marker into the content flow for explicitly-left-aligned blocks
    // too, which manifested as bullets shifting away from the outside
    // gutter in the exported PDF even when the user hadn't asked for it.
    const dataAlign = align && align !== 'left' ? align : undefined;
    return (
        <Tag style={ulStyle} start={listStart}>
            <li style={liStyle} data-align={dataAlign}>
                {props.children}
            </li>
        </Tag>
    );
}
